import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { PrimeIcons } from 'primereact/api';
import { exportToExcel } from '../../utils/exportUtils';
import { TableColumn } from '../../types/responsiveTable';
import './ExportButton.scss';

interface ExportButtonProps<T extends Record<string, any>> {
  fetchData: () => Promise<T[]>;
  columns: TableColumn<T>[];
  fileName: string;
  disabled: boolean;
  additionalColumns?: TableColumn<T>[]; // Columnas adicionales solo para exportación
}

/**
 * Botón para exportar datos a Excel
 *
 * Este componente exporta TODOS los datos de la tabla, no solo los visibles en la paginación actual: fetchData los pide
 * al servidor con los filtros aplicados (ServerResponsiveTable).
 *
 * @param fetchData - Función async para obtener todos los datos
 * @param additionalColumns - Columnas adicionales que solo se incluirán en la exportación,
 *                           no se mostrarán en la tabla. Útil para incluir campos como descripciones
 *                           o datos adicionales que no queremos mostrar en la interfaz.
 */
export function ExportButton<T extends Record<string, any>>({
  fetchData,
  columns,
  fileName,
  disabled,
  additionalColumns = [],
}: ExportButtonProps<T>) {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    try {
      setIsExporting(true);

      const dataToExport = await fetchData();
      const allColumnsForExport = [...columns, ...additionalColumns];

      await exportToExcel(dataToExport, allColumnsForExport, fileName);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const isDisabled = disabled || isExporting;

  return (
    <Button
      label='Exportar Excel'
      icon={isExporting ? PrimeIcons.SPINNER : PrimeIcons.FILE_EXCEL}
      iconPos='left'
      outlined
      severity='info'
      onClick={handleExport}
      disabled={isDisabled}
      loading={isExporting}
      className='export-button'
    />
  );
}
